import httpClient from './http/httpClient';
import storageService from './storageService';

/**
 * Unified Storage Service
 *
 * This service provides the foundation for the new Server-Side Encryption (SSE)
 * and unified storage architecture. It completely bypasses all legacy
 * Client-Side Encryption (CSE) logic.
 *
 * Use this service for all new features (like Procurement) to ensure high
 * performance, modularity, and enterprise-grade reliability.
 */
class UnifiedStorageService {
  /**
   * Uploads a file directly to the configured storage backend (Firebase or SharePoint)
   * in plaintext. Encryption at rest is handled entirely by the backend infrastructure
   * (e.g., Google Cloud Storage CMEK or SharePoint default encryption).
   *
   * @param path The full storage path (e.g., 'clients/{clientId}/procurements/...')
   * @param file The File object to upload
   * @returns The raw storage URI
   */
  async uploadFile(path: string, file: File): Promise<string> {
    // We temporarily bypass the CSE flag in the legacy storage service to upload raw bytes.
    // In Phase 3 of the Unified Encryption migration, this will fully replace the old storage logic.
    const originalEncryptionEnabled = storageService.isEncryptionEnabled();
    try {
      storageService.setEncryptionEnabled(false);
      const result = await storageService.uploadFile(path, file);
      if (!result.success) {
        throw new Error('Upload failed');
      }
      return result.path;
    } finally {
      storageService.setEncryptionEnabled(originalEncryptionEnabled);
    }
  }

  /**
   * Retrieves a secure, short-lived download URL from the backend for any authorized file.
   * This handles both Google Cloud Storage Signed URLs and SharePoint temporary URLs natively.
   *
   * @param gsUri The storage URI (e.g., 'gs://bucket/path' or 'sharepoint://root/bidlogiq/path')
   * @returns The temporary, unauthenticated download URL
   */
  async getSecureDownloadUrl(gsUri: string): Promise<string> {
    const response = await httpClient.post<{
      success: boolean;
      downloadUrl: string;
      error?: string;
    }>('/getTenderFileDownloadUrl', { gsURI: gsUri });

    if (response.success && response.downloadUrl) {
      return response.downloadUrl;
    }

    throw new Error(response.error || 'Failed to fetch secure download URL');
  }

  /**
   * Helper utility to trigger a browser download from a secure URL.
   */
  async triggerBrowserDownload(gsUri: string): Promise<void> {
    const downloadUrl = await this.getSecureDownloadUrl(gsUri);
    window.open(downloadUrl, '_blank');
  }
}

export const unifiedStorageService = new UnifiedStorageService();
